import React, { useState } from "react";
import EmptyCalculator from "./EmptyCalculator";
import CustomInput from "./CustomInput";

const PivotTable = ({ tableData }) => {
  const headers = ["S3", "S2", "S1", "PP", "R1", "R2", "R3"];
  const keys = ["s3", "s2", "s1", "pp", "r1", "r2", "r3"];

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="text-xs font-medium text-n400">
            <th className="py-2 px-4 text-left">Type</th>
            {headers.map((header) => (
              <th key={header} className="py-2 px-4 text-left">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableData.map((row, index) => (
            <tr key={index} className="border-t border-n200">
              <td className="py-3 px-4 font-semibold text-sm text-p300">
                {row.pivotlevel_name}
              </td>
              {keys.map((key) => (
                <td key={key} className="py-3 px-4 text-sm text-p300">
                  {row[key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PivotPointsCalculator = () => {
  const [high, setHigh] = useState("");
  const [low, setLow] = useState("");
  const [open, setOpen] = useState("");
  const [close, setClose] = useState("");

  const [pivotLevels, setPivotLevels] = useState([
    {
      pivotlevel_name: "Classic",
      s3: "-",
      s2: "-",
      s1: "-",
      pp: "-",
      r1: "-",
      r2: "-",
      r3: "-",
    },
    {
      pivotlevel_name: "Woodie's",
      s3: "-",
      s2: "-",
      s1: "-",
      pp: "-",
      r1: "-",
      r2: "-",
      r3: "-",
    },
    {
      pivotlevel_name: "Fibonacci",
      s3: "-",
      s2: "-",
      s1: "-",
      pp: "-",
      r1: "-",
      r2: "-",
      r3: "-",
    },
    {
      pivotlevel_name: "Camarilla",
      s3: "-",
      s2: "-",
      s1: "-",
      pp: "-",
      r1: "-",
      r2: "-",
      r3: "-",
    },
  ]);

  const calculatePivotPoints = () => {
    const h = parseFloat(high);
    const l = parseFloat(low);
    const o = parseFloat(open);
    const c = parseFloat(close);

    if (isNaN(h) || isNaN(l) || isNaN(o) || isNaN(c)) {
      setPivotLevels((prev) =>
        prev.map((level) => ({
          ...level,
          s3: "-",
          s2: "-",
          s1: "-",
          pp: "-",
          r1: "-",
          r2: "-",
          r3: "-",
        }))
      );
      return;
    }

    const cprPivot = (h + l + c) / 3;
    const classic = {
      pivotlevel_name: "Classic",
      s3: (l - 2 * (h - cprPivot)).toFixed(2),
      s2: (cprPivot - (h - l)).toFixed(2),
      s1: (2 * cprPivot - h).toFixed(2),
      pp: cprPivot.toFixed(2),
      r1: (2 * cprPivot - l).toFixed(2),
      r2: (cprPivot + (h - l)).toFixed(2),
      r3: (h + 2 * (cprPivot - l)).toFixed(2),
    };

    const woodiesPivot = (h + l + o * 2) / 4;
    const woodies = {
      pivotlevel_name: "Woodie's",
      s3: (l - 2 * (h - woodiesPivot)).toFixed(2),
      s2: (woodiesPivot - (h - l)).toFixed(2),
      s1: (2 * woodiesPivot - h).toFixed(2),
      pp: woodiesPivot.toFixed(2),
      r1: (2 * woodiesPivot - l).toFixed(2),
      r2: (woodiesPivot + (h - l)).toFixed(2),
      r3: (h + 2 * (woodiesPivot - l)).toFixed(2),
    };

    const fib = {
      pivotlevel_name: "Fibonacci",
      s3: (cprPivot - (h - l)).toFixed(2),
      s2: (cprPivot - 0.618 * (h - l)).toFixed(2),
      s1: (cprPivot - 0.382 * (h - l)).toFixed(2),
      pp: cprPivot.toFixed(2),
      r1: (cprPivot + 0.382 * (h - l)).toFixed(2),
      r2: (cprPivot + 0.618 * (h - l)).toFixed(2),
      r3: (cprPivot + (h - l)).toFixed(2),
    };

    const camarilla = {
      pivotlevel_name: "Camarilla",
      s3: (c - (h - l) * 1.25).toFixed(2),
      s2: (c - (h - l) * 1.1666).toFixed(2),
      s1: (c - (h - l) * 1.0833).toFixed(2),
      pp: cprPivot.toFixed(2),
      r1: (c + (h - l) * 1.0833).toFixed(2),
      r2: (c + (h - l) * 1.1666).toFixed(2),
      r3: (c + (h - l) * 1.25).toFixed(2),
    };

    setPivotLevels([classic, woodies, fib, camarilla]);
  };

  const hasResults = pivotLevels.some((level) => level.pp !== "-");

  return (
    <div className="flex gap-4 w-full">
      {/* Input Card */}
      <div className="bg-p400 rounded shadow-md w-1/2 p-4">
        <CustomInput
          label="Open"
          value={open}
          onChange={(e) => setOpen(e.target.value)}
          isNumberInput={true}
        />

        <CustomInput
          label="High"
          value={high}
          onChange={(e) => setHigh(e.target.value)}
          isNumberInput={true}
        />

        <CustomInput
          label="Low"
          value={low}
          onChange={(e) => setLow(e.target.value)}
          isNumberInput={true}
        />

        <CustomInput
          label="Close"
          value={close}
          onChange={(e) => setClose(e.target.value)}
          isNumberInput={true}
        />

        <button
          className="w-full py-2.5 bg-swipe-button text-xs font-medium text-p400 rounded my-3"
          onClick={calculatePivotPoints}>
          Calculate
        </button>
      </div>

      {/* Results Card */}
      <div className="bg-p400 rounded shadow-md w-1/2">
        {!hasResults ? (
          <EmptyCalculator />
        ) : (
          <div className="p-4">
            <h3 className="text-md font-semibold mb-4 ml-3">Pivot Points:</h3>
            <PivotTable tableData={pivotLevels} />
          </div>
        )}
      </div>
    </div>
  );
};

export default PivotPointsCalculator;
